<template>
  <div class="flex-button">
    <div v-if="!editCustomer">
      <customer
        :type="customer.type"
        :name="customer.name ? customer.name : newCustomer.name"
        :phone="Utils.phoneMask(customer.phone)"
        :birthday="customer.birthdate ? Utils.db2date(customer.birthdate) : ''"
        :email="customer.email || ''"
      />

      <base-card
        v-if="!!this.annotation && !!this.annotation.id"
        class="notes-card p_20"
      >
        <p class="notes-title">Anotações</p>
        <p class="notes-text p_0">{{ this.annotation.annotation }}</p>
      </base-card>

      <base-card
        class="relative"
        v-for="(address, addressIndex) in customer.addresses"
        :key="addressIndex"
      >
        <div class="info-customer-address d-flex align-center">
          <p class="mr_20 fs-12 o-50">
            <i class="fal fa-map-marker-alt"></i>
          </p>
          <div class="w-100">
            <p class="w-100">{{ formatAddressString(address) }}</p>
            <div class="address-datas">
              <span>{{ distanceTo(address.geolocation) }}</span>
              <span>{{ deliveryTax(address.geolocation) }}</span>
            </div>
          </div>
        </div>
      </base-card>
    </div>

    <div v-else>
      <base-card>
        <base-label text="Informações do cliente" class="mb_30" />
        <base-input
          label="Nome completo"
          v-model="newCustomer.name"
          autocomplete="none"
        />
        <base-input
          label="Telefone"
          v-model="newCustomer.phone"
          :mask="
            newCustomer.phone.charAt(2) == '9'
              ? ['(##) #####-####']
              : ['(##) ####-####']
          "
          :disabled="!!customer.id"
          autocomplete="none"
        />
        <base-input
          label="E-mail"
          v-model="newCustomer.email"
          autocomplete="none"
        />
        <base-input
          label="Aniversário"
          type="tel"
          class="mb_0"
          v-model="newCustomer.birthdate"
          autocomplete="none"
          :mask="StringLanguage.dateMask"
          :masked="true"
        />
      </base-card>

      <base-card>
        <base-label text="Anotações" />
        <base-textarea class="mb_5" rows="4" v-model="annotation.annotation" />
      </base-card>

      <div
        v-for="(address, addressIndex) in addresses"
        :key="`${addressIndex}`"
      >
        <base-card
          class="relative"
          v-if="address.id"
          :key="`addrCard-${addressIndex}`"
        >
          <base-label :text="`Endereço ${addressIndex + 1}`" class="mb_25" />
          <span
            class="delete-address cursor-pointer"
            @click="removeAddress(addressIndex)"
          >
            <i class="fal fa-times"></i>
          </span>

          <base-question :text="formatAddressString(address)" class="mb_5" />
        </base-card>

        <base-card
          v-else
          class="relative pb_10"
          :key="`addrForm-${addressIndex}`"
        >
          <base-label :text="`Novo endereço`" class="mb_30" />
          <span
            class="delete-address cursor-pointer"
            @click="removeAddress(addressIndex, false)"
          >
            <i class="fal fa-times"></i>
          </span>

          <address-input
            placeholder="Buscar endereço e número"
            @selectedAddress="
              (option) => onAddressSelected(option, addressIndex)
            "
            :disabled="!!address.geolocation"
          />

          <!-- Esta div só aparece quando um endereço for selecionado no select acima -->
          <div v-if="!!address.geolocation">
            <div class="row">
              <div class="col-lg-12 col-xs-12">
                <base-input label="Complemento" v-model="address.complement" />
              </div>
              <div class="col-lg-12 col-xs-12">
                <base-input
                  label="Ponto de referência"
                  v-model="address.observation"
                />
              </div>
            </div>
          </div>
        </base-card>
      </div>

      <base-add
        :text="'Endereço'"
        :height="'120px'"
        design="two"
        @click.native="addresses.push(new Location())"
      />
      <div v-if="showActions" class="mt_15">
        <base-button
          v-if="editCustomer"
          :text="'Salvar'"
          @click="save()"
          class="w-100 big mb_20"
        />
      </div>
    </div>
    <div v-if="showActions">
      <base-button
        v-if="!editCustomer"
        :text="'Editar'"
        @click="editMode()"
        class="w-100 big"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Geolocation from 'easyjs/lib/Schema/Address/Geolocation';
import Delivery from 'easyjs/lib/Schema/Delivery/index';
import BaseInput from '@/components/Input';
import BaseCard from '@/components/Card';
import BaseQuestion from '@/components/Question';
import BaseButton from '@/components/Button';
import BaseAdd from '@/components/Add';
import BaseLabel from '@/components/Label';
import BaseTextarea from '@/components/Textarea';
import BaseDrawer from '@/components/Drawer';

import AddressInput from '@/containers/AddressInput';

import Utils from '@/utils';
import AccountApi from '@/api/account';
import UserApi from '@/api/user';
import CustomerApi from '@/api/customer';

import Customer from '@/containers/Customer';
import Location from '@/plugins/core/schema/configurations/BasicInfo/Location';

export default {
  props: {
    configurations: {},
    role: {},
    customer: {},
    showActions: { type: Boolean, required: false, default: true },
  },
  data() {
    return {
      Location,
      Utils,
      editCustomer: false,
      newCustomer: {},
      addresses: [],
      annotation: { annotation: null },
      lastScheduledSearch: [],
      addressString: [],
      addressOptions: [],
    };
  },
  components: {
    BaseInput,
    BaseCard,
    BaseQuestion,
    BaseButton,
    BaseAdd,
    BaseLabel,
    BaseTextarea,
    BaseDrawer,
    AddressInput,
    Customer,
  },
  mounted() {
    this.newCustomer = JSON.parse(JSON.stringify(this.customer));
    if (this.newCustomer.birthdate) {
      this.newCustomer.birthdate = Utils.db2date(this.newCustomer.birthdate);
    }

    this.addresses = this.customer.addresses
      ? Object.clone(this.customer.addresses)
      : [];
    this.annotation = this.customer.annotation
      ? Object.clone(this.customer.annotation)
      : { annotation: null };
    if (!this.customer.name) {
      this.editCustomer = true;
    }
  },
  methods: {
    editMode() {
      this.sourceCustomer = JSON.parse(JSON.stringify(this.customer.type));

      if (this.sourceCustomer != 'M' && this.sourceCustomer != 'F') {
        this.newCustomer = JSON.parse(JSON.stringify(this.customer));
        if (this.newCustomer.birthdate) {
          this.newCustomer.birthdate = Utils.db2date(
            this.newCustomer.birthdate
          );
        }
        this.addresses = this.customer.addresses
          ? Object.clone(this.customer.addresses)
          : [];
        this.editCustomer = true;
      }
    },
    distanceTo(addressGeolocation) {
      if (!addressGeolocation) {
        return ' - ';
      }

      const restaurantGeo = Geolocation.fromObject(
        this.configurations.location.geolocation
      );
      if (
        restaurantGeo.latitude == addressGeolocation.latitude &&
        restaurantGeo.longitude == addressGeolocation.longitude
      ) {
        return Utils.convertMoney(0, '', 'Km');
      }

      return Utils.convertMoney(
        restaurantGeo.distanceTo(addressGeolocation) / 1000,
        '',
        'Km'
      );
    },
    deliveryTax(addressGeolocation) {
      if (!addressGeolocation) {
        return ' - ';
      }

      const restaurantGeo = Geolocation.fromObject(
        this.configurations.location.geolocation
      );
      const samePlace =
        restaurantGeo.latitude == addressGeolocation.latitude &&
        restaurantGeo.longitude == addressGeolocation.longitude;
      const distance = samePlace
        ? 0
        : restaurantGeo.distanceTo(addressGeolocation) / 1000;

      const deliveryRegions = this.configurations.delivery_region;
      const radiusStrips = deliveryRegions.filter(
        (region) => +region.up_to >= +distance
      );

      radiusStrips.sort((a, b) => +a.up_to <= +b.up_to);

      if (radiusStrips[0] && !!radiusStrips[0].free_fee) {
        return 'Taxa grátis';
      }

      return radiusStrips[0]
        ? Utils.convertMoney(+radiusStrips[0].price)
        : 'Fora da região de entrega';
    },
    formatAddressString(location, inverseNumber = false) {
      let addressString = `${location.address}${
        location.number !== 'null' ? `, ${location.number}` : ''
      }`;

      if (inverseNumber) {
        addressString = `${location.number ? `${location.number}, ` : ''}${
          location.address
        }`;
      }

      if (location.complement) {
        addressString += `, (${location.complement})`;
      }

      if (location.region || location.city) {
        addressString += ` - ${
          location.region
            ? `${location.region}${location.city ? `, ${location.city}` : ''}`
            : `${location.city}`
        }`;
      }

      if (location.state || location.country) {
        addressString += ` - ${
          location.state
            ? `${location.state}${
                location.country ? `, ${location.country}` : ''
              }`
            : `${location.country}`
        }`;
      }

      if (location.zipcode) {
        addressString += ` - ${location.zipcode}`;
      }

      if (location.observation) {
        addressString += ` - (${location.observation})`;
      }

      return addressString;
    },
    onAddressSelected(option, index) {
      if (option.address) {
        this.addresses[index] = Location.fromObject(option.address);
        this.$forceUpdate();
      }
    },
    updateAddress(index) {
      const searchAddress = this.addresses[index];

      if (!searchAddress.number) {
        return alert('Número não informado!');
      }

      Vue.set(this.addresses, index, Location.fromObject(searchAddress));
    },
    removeAddress(index, exitEditCustomer = true) {
      const self = this;
      confirm('Deseja apagar o endereço?', (response) => {
        if (response) {
          if (self.addresses[index] && self.addresses[index].id) {
            self.$emit('removeAddress', self.addresses[index].id);
          }
          self.addresses.splice(index, 1);
          self.editCustomer = !exitEditCustomer;
          self.$forceUpdate();
        }
      });
    },
    save() {
      const { newCustomer, customer, editCustomer, addresses } = this;
      if (editCustomer) {
        if (!newCustomer.name || !newCustomer.name.trim()) {
          return alert('Nome não informado!');
        }

        if (!newCustomer.phone || !newCustomer.phone.trim()) {
          return alert('Número não informado!');
        }

        if (newCustomer.phone.length < 10 || newCustomer.phone.length > 11) {
          return alert('Telefone inválido! Verifique a quantidade de dígitos.');
        }

        newCustomer.phone = newCustomer.phone.replace(/[\(\)\-\ ]*/g, '');
        newCustomer.addresses = addresses;

        this.saveCustomer(Object.clone(newCustomer));
      }
    },
    async updateAfterCustomerSuccess() {
      this.editCustomer = !this.editCustomer;
      this.$forceUpdate();
    },
    async saveCustomer(customer) {
      const addresses = Object.clone(customer.addresses);
      const annotation = Object.clone(this.annotation);

      delete customer.addresses;
      delete customer.annotation;

      let isSuccessCustomer = false;
      let newCustomer = null;

      // novo cliente
      if (!customer.id) {
        const userResponse = await UserApi.associateCustomer({
          ...customer,
          birthdate: customer.birthdate
            ? Utils.br2Iso(customer.birthdate)
            : null,
        });
        if (userResponse.getErrors([]).length > 0) {
          alert(
            userResponse.getErrors(['Ocorreu um erro ao salvar o cliente!'])[0]
          );
          return;
        }
        const userData = userResponse.getData({});
        if (userData.id) {
          this.$emit('setCustomer', customer);
          newCustomer = userData;
          this.updateAfterCustomerSuccess();
          isSuccessCustomer = true;
          customer.id = newCustomer.id;
        }
      } else {
        // edit
        const { id } = customer;
        delete customer.id;
        const userResponse = await UserApi.customer(id, {
          ...customer,
          birthdate: customer.birthdate
            ? Utils.br2Iso(customer.birthdate)
            : null,
        });
        if (userResponse.getErrors([]).length > 0) {
          alert(
            userResponse.getErrors(['Ocorreu um erro ao salvar o cliente!'])[0]
          );
          return;
        }
        const userData = userResponse.getData({});
        if (userData.id) {
          this.$emit('setCustomer', customer);
          newCustomer = userData;
          this.updateAfterCustomerSuccess();
          isSuccessCustomer = true;
          customer.id = newCustomer.id;
        }
      }

      if (!newCustomer) {
        return;
      }

      if (isSuccessCustomer) {
        isSuccessCustomer = await this.saveAnnotation(
          annotation.annotation,
          customer.id
        );
      }

      if (isSuccessCustomer) {
        await this.saveAddresses(addresses, customer.id);
      }
    },
    async saveAnnotation(annotation, id) {
      const customerAnnotation = this.customer.annotation;
      if (!!customerAnnotation && customerAnnotation.id) {
        // Se já existir anotação
        if (annotation) {
          // Se a anotação for definida
          if (customerAnnotation.annotation != annotation) {
            // Se a anotação definida for diferente da existente
            // Salvar anotação
            const response = await CustomerApi.addAnnotation(id, annotation);
            return !!response.getData({ id: null }).id;
          }
        } else {
          // Se a anotação não for definida
          const response = await CustomerApi.deleteAnnotation(id);
          return response.getCode(500) == 200;
        }
      } else {
        // Se não existir anotação
        if (annotation) {
          // Se a anotação for definida
          // Salvar anotação
          const response = await CustomerApi.addAnnotation(id, annotation);
          return !!response.getData({ id: null }).id;
        }
      }

      return true;
    },
    async saveAddresses(addresses, id) {
      for (const addressIndex in addresses) {
        const address = addresses[addressIndex];

        if (typeof address !== 'object') {
          continue;
        }

        delete address.countryCode;
        delete address.realPosition;

        address.region = address.district;

        delete address.district;
        address.number += '';

        if (address.id) {
          continue;
        }

        const response = await AccountApi.addUserAddress(
          id,
          address,
          addressIndex
        );
        if (response.getErrors([]).length > 0) {
          if (!address.geolocation) {
            alert(response.getErrors([])[0]);
          }
        }
      }

      this.$emit('getAddresses', id);
    },
    getAddresses(userId, index = '') {
      CustomerApi.GetConfigurations(userId, false).then((configurations) => {
        const addressesData = configurations.getData({
          addresses: [],
        }).addresses;
        this.$set(this.selectedCustomer, 'addresses', addressesData);

        if (addressesData.length > 0) {
          this.selectedDeliveryAddress = 0;
        }

        if (index != '') {
          this.selectedDeliveryAddress = index;
        }
      });
    },
  },
  watch: {
    customer: {
      deep: true,
      handler(newValue) {
        if (newValue.id) {
          this.newCustomer.id = newValue.id;
        }

        Vue.set(this, 'addresses', Object.clone(newValue.addresses || []));
        Vue.set(
          this,
          'annotation',
          Object.clone(newValue.annotation || { annotation: null })
        );
      },
    },
  },
};
</script>

style="display: flex, justify-content: space-between, height: 100%"

<style scoped>
.flex-button {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 98%;
}

.info-customer-address,
.notes-text {
  padding: 5px;
  font-size: 1.4em;
  color: #999;
  font-weight: 300;
}

.delete-address {
  position: absolute;
  top: 26px;
  right: 0;
  font-size: 1.4em;
  color: #aaaaab;
  cursor: pointer;
  padding: 5px;
}

.address-datas {
  margin-top: 20px;
}

.address-datas span {
  background: #eee;
  padding: 3px 8px;
  margin-right: 10px;
  font-weight: 400;
  color: #aaa;
}

.notes-title {
  font-size: 1.6em;
  color: #888;
  font-weight: 600;
  margin-bottom: 15px;
}

.field-title {
  color: #999;
  font-weight: 600;
  font-size: 1.3em;
  margin-bottom: 7px;
}

.field-title + p {
  font-size: 1.3em;
  color: #bbb;
}
</style>
